import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompoApp } from '../../../../services/CompoApp';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import Constants from '../../../../Constants';
import { AuthserviceService } from '../login-page/authservice.service';
import ErrorCode from '../../../../enums/ErrorCode';
import { TranslateModule } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { UnsubscribeBaseModel } from '../../../../base/model/UnsubscribeBase.model';
import { takeUntil } from 'rxjs/operators';
import { InvitationRequestService } from '../../../../services/invitation-request.service';
import { SseSingletonService } from '../../../../services/sse/sse-singleton.service';
import { PropagateOpts } from '../../../../models/form/base-cp-form.model';
import { AuthPageCommonComponent } from '../auth-page-common.component';
import { FormInputComponent } from '../../../components/input/form-input/form-input.component';
import { ClickOutsideDirective } from '../../../../directives/click-outside/click-outside.directive';
import {
  PasswordRequirementsComponent
} from '../../../components/password-requirements/password-requirements.component';
import { InlineButtonComponent } from '../../../globals/button/inline-button/inline-button.component';
import { ButtonComponent } from '../../../globals/button/button/button.component';
import {
  MessageDesignBSmallComponent
} from '../../../components/message-design-b/message-design-b-small/message-design-b-small.component';
import { JwtAuthenticationResponse } from '../login-page/jwt-authentication-response.interface';


/**
 * RegisterUserPageComponent used for following user case:
 * Admin user create a new user in the organization with an invitation link.
 * User get the invitation email, click the link and finish the registration
 */
@Component({
  selector: 'cp-register-user-page',
  templateUrl: 'register-user-page.component.html',
  styleUrls: ['register-user-page.component.scss', '../auth-page-common.scss'],
  imports: [AuthPageCommonComponent, FormInputComponent, ClickOutsideDirective, PasswordRequirementsComponent, InlineButtonComponent, ButtonComponent, MessageDesignBSmallComponent, TranslateModule],
  providers: [AuthserviceService]
})
export class RegisterUserPageComponent extends UnsubscribeBaseModel implements OnInit {

  //region READONLY FIELDS
  readonly PASSWORD_MIN_LENGTH: number = 8;
  //endregion

  //region INJECTIONS
  private sseSingletonService: SseSingletonService = inject(SseSingletonService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private authService: AuthserviceService = inject(AuthserviceService);
  private titleService: Title = inject(Title);
  private router: Router = inject(Router);
  private app: CompoApp = inject(CompoApp);
  private invitationRequestService: InvitationRequestService = inject(InvitationRequestService);
  //endregion

  //region FORM
  readonly formGroup: UntypedFormGroup = new UntypedFormGroup({});
  readonly EMAIL: string = 'email';
  readonly PASSWORD: string = 'password';
  readonly GENERAL_CONDITION: string = 'generalCondition';
  //endregion

  //region FIELDS
  uuid: string;
  userId: number;
  email: string;
  invalidInvitation: boolean = false;
  showInvalidMessage: boolean = false;
  wrongUserNamePwd: boolean;
  showPasswordInputErrors: boolean = false;
  passwordValidatorsSet: boolean = false;
  inProgress: boolean = false;
  //endregion

  //region GETTERS
  get isValid(): boolean {
    if (!this.formGroup) {
      return false;
    }

    return this.formGroup?.valid;
  }

  //endregion

  //region LIFECYCLE
  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(param => {
        this.uuid = param['uuid'];
        this.invitationRequestService.validateInvitationUuid(this.uuid)
          .subscribe(resp => {
            if (!!resp.valid) {
              this.email = resp.email;
              this.userId = resp.userId;
              this.createFormGroup(this.email);
            } else {
              this.showInvalidMessage = true;
              this.invalidInvitation = true;

              this.formGroup?.get(this.PASSWORD)?.disable();
              //navigate to login page in 10 Seconds
              setTimeout(() => {
                //navigate to login page
                this.router.navigate(['']);
              }, 10000);
            }
          });
      });

    this.formGroup.valueChanges
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((value: any) => {
        if (value) {
          const passwordValue: string = value[this.PASSWORD];
          if (this.showPasswordInputErrors && (!passwordValue || passwordValue.length === 0)) {
            this.showPasswordInputErrors = false;
          }
          if (!this.passwordValidatorsSet && passwordValue && passwordValue.length > 0) {
            this.setPasswordValidators();
          }
        }
      });
  }

  //endregion

  //region METHODS
  onPasswordInputLostFocus(): void {
    const password: string = this.formGroup.get(this.PASSWORD).value;
    if (password && password.length > 0 && !this.showPasswordInputErrors) {
      this.showPasswordInputErrors = true;
    }
  }

  onGoToLoginPage() {
    this.router.navigate(['']);
  }

  onRegisterUser() {
    if (!this.isValid || this.inProgress || this.invalidInvitation) {
      return;
    }

    const login: string = this.formGroup.get(this.EMAIL).value;
    const password: string = this.formGroup.get(this.PASSWORD).value;

    if (!password
      || !password.length
      || password.length < this.PASSWORD_MIN_LENGTH
      || !password.match(Constants.PASSWORD_REGEX)
    ) {
      this.setPasswordValidators();
      return;
    }

    this.inProgress = true;
    this.formGroup?.get(this.PASSWORD)?.disable(PropagateOpts.EMIT_EVENT_FALSE);

    this.authService.register(login, password, this.uuid)
      .subscribe({
        next: (result: JwtAuthenticationResponse) => {
          if (!result?.token) {
            return;
          }
          this.authService.initAccessModeFromRoleId(result.selectedUserRoleId);

          let pendingUrl: string;
          if (result.roleCount <= 1) {
            pendingUrl = this.app.data.getPendingUrl();
            this.app.data.setPendingUrl(null);
          }

          this.sseSingletonService.disconnect();
          this.sseSingletonService.connect();
          this.titleService.setTitle('Objack');

          if (this.app.data.getUser().isResetPasswordOnLogin) {
            this.router.navigate(['/resetPassword/' + this.app.data.getUser().id]);
          } else if (result.roleCount > 1) {
            this.router.navigate(['/selectRole'], { queryParams: { roleCount: result.roleCount } });
          } else {
            setTimeout(() => {
              this.app.on.loginTrigger.next(this.app.data.getUser());
              this.router.navigate([pendingUrl || '/home/prozesse/jobs']);
            }, 1000);
          }
        },
        error: (error: any) => {
          if (error.status === 401) {
            if (error.error && error.error.code && error.error.code === ErrorCode.WRONG_USERNAME_OR_PASSWORD) {
              this.wrongUserNamePwd = true;
            }
            this.formGroup.reset();
          }
          this.inProgress = false;
          this.formGroup?.get(this.PASSWORD)?.enable(PropagateOpts.EMIT_EVENT_FALSE);
        }
      });

  }

  private createFormGroup(email: string) {
    this.formGroup.setControl(
      this.EMAIL,
      new UntypedFormControl(email, Validators.required)
    );
    this.formGroup.disable();
    this.formGroup.setControl(this.PASSWORD, new UntypedFormControl(null));
    this.formGroup.setControl(this.GENERAL_CONDITION, new UntypedFormControl(false));
    this.passwordValidatorsSet = false;
  }

  private setPasswordValidators(): void {
    const passwordControl: FormControl = this.formGroup.get(this.PASSWORD) as FormControl;
    if (passwordControl) {
      passwordControl.setValidators(
        [
          Validators.required, Validators.minLength(8),
          Validators.pattern(Constants.PASSWORD_REGEX)
        ]
      );
      passwordControl.updateValueAndValidity(PropagateOpts.EMIT_EVENT_FALSE);
      this.passwordValidatorsSet = true;
    }
  }

  //endregion

}
